/**
 * Promoting a lead into the registry.
 *
 * `CompanyCreate` has twenty-six fields and requires exactly one, `name`. The
 * other twenty-five carry defaults, and a form that omits a field does not
 * leave it blank — it writes the default. So the question this form answers is
 * not "which fields exist" but "which defaults would be a claim nobody made".
 *
 * Measured against the seventy-five curated companies:
 *
 *   physicalFeedbackLevel = 0     matched  0 of 75   (the lowest recorded is 1)
 *   inUniverseLikelihood  = 0.5   matched  0 of 75   (none is at or below 0.5)
 *   cohort                = emerging       17 of 75
 *   headquarters.country  = US             65 of 75
 *   status                = active         70 of 75
 *
 * The first two have never once been right. A form that omitted them would
 * write, on every promotion, a value no reviewer has ever chosen — so those
 * three are stated deliberately here, with no preselection to inherit. The last
 * two are usually right and are preselected *visibly*, which is the difference
 * between a default a reviewer accepted and one they never saw.
 *
 * Everything else is left out on purpose. `description`, `websiteUrl` and the
 * classification axes carry honest absences — an empty list claims nothing —
 * and the record enters at `reviewStatus: pending`, so completing it is the
 * review step's job. `paradigms` is omitted from the axes for the same reason
 * the schema does not require it: twenty-four of the seventy-five record none,
 * and a company need not disclose its architecture.
 *
 * The name is deliberately not prefilled from the lead. `docs/discovery.md` is
 * explicit that a `nameHint` may be a description rather than a name, and this
 * route's docstring says nothing is derived from the lead for that reason —
 * prefilling would file a description as a registry name.
 */
import { useState } from 'react';
import { usePromoteLead } from '../hooks/useDecision';
import { useTaxonomy } from '../hooks/useRadarQuery';
import { label } from '../lib/format';
import { CheckboxFilter } from './CheckboxFilter';

/** Defaults that are usually right, shown so accepting one is a choice. */
const PRESELECTED = { country: 'US', status: 'active' };

/** How likely the subject is to belong, as the curated corpus actually scores. */
const LIKELIHOODS = ['0.6', '0.7', '0.8', '0.85', '0.9', '0.95'];

export function PromoteForm({ candidateId, nameHint }: { candidateId: string; nameHint: string }) {
  const taxonomy = useTaxonomy();
  const promote = usePromoteLead(candidateId);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [country, setCountry] = useState(PRESELECTED.country);
  const [status, setStatus] = useState(PRESELECTED.status);
  const [cohort, setCohort] = useState('');
  const [physicalFeedbackLevel, setFeedback] = useState('');
  const [inUniverseLikelihood, setLikelihood] = useState('');
  const [domains, setDomains] = useState<string[]>([]);
  const [tasks, setTasks] = useState<string[]>([]);
  const [scales, setScales] = useState<string[]>([]);

  const published = taxonomy.data;
  // The three whose default has never matched a curated record. Named once, so
  // the guard on the button and the sentence explaining it cannot disagree
  // about which they are.
  const MUST_STATE = [
    ['cohort', cohort],
    ['physical feedback', physicalFeedbackLevel],
    ['in-universe likelihood', inUniverseLikelihood],
  ] as const;
  const unstated = MUST_STATE.filter(([, value]) => !value).map(([named]) => named);

  return (
    <form
      className="decision-form"
      onSubmit={(submitted) => {
        submitted.preventDefault();
        promote.mutate({
          name,
          description,
          websiteUrl: websiteUrl || null,
          headquarters: { country },
          status,
          cohort,
          physicalFeedbackLevel: Number(physicalFeedbackLevel),
          inUniverseLikelihood: Number(inUniverseLikelihood),
          domains,
          tasks,
          scales,
        });
      }}
    >
      <label>
        Registry name
        <input type="text" value={name} onChange={(c) => setName(c.target.value)} />
      </label>
      {/* Context, never a value. The hint is what the lead was found as. */}
      <small className="muted">Found as: {nameHint}</small>
      <label>
        Description
        <input type="text" value={description} onChange={(c) => setDescription(c.target.value)} />
      </label>
      <label>
        Website
        <input type="text" value={websiteUrl} onChange={(c) => setWebsiteUrl(c.target.value)} />
      </label>
      <label>
        Country
        <input type="text" value={country} onChange={(c) => setCountry(c.target.value)} />
      </label>
      <label>
        Status
        <select value={status} onChange={(c) => setStatus(c.target.value)}>
          {(published?.companyStatuses ?? []).map((value) => (
            <option key={value} value={value}>{label(value)}</option>
          ))}
        </select>
      </label>
      <label>
        Cohort
        <select value={cohort} onChange={(c) => setCohort(c.target.value)}>
          <option value="">Choose a cohort…</option>
          {(published?.cohorts ?? []).map((value) => (
            <option key={value} value={value}>{label(value)}</option>
          ))}
        </select>
      </label>
      <label>
        Physical feedback
        <select
          value={physicalFeedbackLevel}
          onChange={(c) => setFeedback(c.target.value)}
        >
          <option value="">Choose a level…</option>
          {(published?.physicalFeedbackLevels ?? []).map((value) => (
            <option key={String(value)} value={String(value)}>{String(value)}</option>
          ))}
        </select>
      </label>
      <label>
        In-universe likelihood
        <select
          value={inUniverseLikelihood}
          onChange={(c) => setLikelihood(c.target.value)}
        >
          <option value="">Choose a likelihood…</option>
          {LIKELIHOODS.map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
      </label>
      <CheckboxFilter
        legend="Domains" emptyLabel="No domain recorded"
        options={published?.domains ?? []} selected={domains} onChange={setDomains}
      />
      <CheckboxFilter
        legend="Tasks" emptyLabel="No task recorded"
        options={published?.tasks ?? []} selected={tasks} onChange={setTasks}
      />
      <CheckboxFilter
        legend="Scales" emptyLabel="No scale recorded"
        options={published?.scales ?? []} selected={scales} onChange={setScales}
      />
      <button className="button" type="submit" disabled={promote.isPending || unstated.length > 0}>
        Promote to registry
      </button>
      {/* Named, because a disabled button with no reason is indistinguishable
          from a broken one. These three have no honest default to fall back on. */}
      {unstated.length ? (
        <small className="muted">State {unstated.join(', ')} before promoting.</small>
      ) : null}
      {promote.error ? <small className="refusal">{promote.error.message}</small> : null}
    </form>
  );
}
